import {ReactElement, ReactNode, useEffect, useState} from 'react';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {Menu, MenuTrigger} from '@common/ui/navigation/menu/menu-trigger';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {Trans} from '@common/i18n/trans';
import {useTrans} from '@common/i18n/use-trans';
import {IconButton} from '@common/ui/buttons/icon-button';
import {Item} from '@common/ui/forms/listbox/item';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {CustomMenu, MenuItemLabel} from '@common/menus/custom-menu';
import {
  dashboardAiChatNavClassName,
  isDashboardAiChatMenuItem,
} from '@common/menus/dashboard-ai-chat-menu';
import {useSettings} from '@common/core/settings/use-settings';
import {ButtonColor} from '@common/ui/buttons/get-shared-button-style';
import {MenuIcon} from '@common/icons/material/Menu';
import {MenuItemConfig} from '@common/core/settings/settings';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {useDarkThemeVariables} from '@common/ui/themes/use-dark-theme-variables';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {useLightThemeVariables} from '@common/ui/themes/use-light-theme-variables';
import {isAbsoluteUrl} from '@common/utils/urls/is-absolute-url';
import {adminNavbarItems} from '@common/admin/admin-menu-config';
import {NavLink, useLocation} from 'react-router-dom';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    primaryButtonColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  color = color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary';
  darkModeColor =
    darkModeColor ?? darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';

  if (isDarkMode) {
    color = darkModeColor;
  }

  return (
    <div
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && 'h-64 py-8',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
      )}
    >
      <div
        className={clsx(
          'flex h-full items-center justify-end gap-10 pl-14 pr-8 md:pl-20 md:pr-20',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {!hideLogo && (
          <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
        )}
        {toggleButton}
        {children}
        <MobileMenu position={menuPosition} />
        <DesktopMenu position={menuPosition} />
        <div className="ml-auto flex items-center gap-4 md:gap-14">
          {rightChildren}
          {showNotifButton && <NotificationDialogTrigger />}
          {isLoggedIn ? (
            <NavbarAuthUser items={authMenuItems} />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}

function dashboardMenuMatchDescendants(to: string) {
  return to === '/dashboard';
}

/** Статичные классы: isActive в className ломает SSR-гидратацию NavLink. */
const navbarMenuItemClassName =
  'opacity-90 hover:underline hover:opacity-100 aria-[current=page]:opacity-100';

function useAfterHydration(): boolean {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);
  return hydrated;
}

function isDashboardMenuItemActive(action: string, pathname: string): boolean {
  const normalizedPath =
    pathname.endsWith('/') && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;
  const normalizedAction =
    action.endsWith('/') && action.length > 1 ? action.slice(0, -1) : action;

  if (normalizedAction === '/dashboard') {
    return normalizedPath === '/dashboard';
  }

  return (
    normalizedPath === normalizedAction ||
    normalizedPath.startsWith(`${normalizedAction}/`)
  );
}

function DesktopMenu({position}: DesktopMenuProps) {
  // Изменено: 2026-01-09 - admin-navbar теперь статичное, не берется из БД
  if (position === 'admin-navbar') {
    return <AdminNavbarMenu />;
  }

  return (
    <CustomMenu
      className={clsx(
        'mx-14 text-sm max-md:hidden',
        position === 'dashboard' && 'min-w-0 shrink overflow-x-auto',
      )}
      gap={position === 'dashboard' ? 'gap-16' : 'gap-30'}
      matchDescendants={
        position === 'dashboard' ? dashboardMenuMatchDescendants : undefined
      }
      itemClassName={({item}) =>
        clsx(
          navbarMenuItemClassName,
          isDashboardAiChatMenuItem(item) && dashboardAiChatNavClassName,
        )
      }
      menu={position}
    />
  );
}

function AdminNavbarMenu() {
  const {hasPermission} = useAuth();

  // Фильтруем пункты меню по правам доступа
  const visibleItems = adminNavbarItems.filter(item => {
    if (!item.permissions || item.permissions.length === 0) {
      return true;
    }
    return item.permissions.every(permission => hasPermission(permission));
  });

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="mx-14 flex gap-20 text-sm max-md:hidden">
      {visibleItems.map(item => (
        <NavLink
          key={item.action}
          to={item.action}
          className={navbarMenuItemClassName}
        >
          <Trans message={item.label} />
        </NavLink>
      ))}
    </div>
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({position}: MobileMenuProps) {
  const {trans} = useTrans();
  const {pathname} = useLocation();
  const hydrated = useAfterHydration();
  // Изменено: 2026-01-09 - admin-navbar теперь статичное, не берется из БД
  if (position === 'admin-navbar') {
    return <AdminNavbarMobileMenu />;
  }

  const navigate = useNavigate();
  const menu = useCustomMenu(position);

  if (!menu?.items.length) {
    return null;
  }

  const handleItemClick = (item: MenuItemConfig) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <MenuTrigger>
      <IconButton
        className="md:hidden"
        aria-label={trans({message: 'Toggle menu'})}
      >
        <MenuIcon />
      </IconButton>
      <Menu>
        {menu.items.map(item => {
          const Icon = item.icon && createSvgIconFromTree(item.icon);
          const isActive =
            position === 'dashboard' &&
            isDashboardMenuItemActive(item.action, pathname);
          return (
            <Item
              value={item.action}
              onSelected={() => handleItemClick(item)}
              key={item.id}
              startIcon={Icon && <Icon />}
              className={clsx(
                hydrated && isActive && 'opacity-100',
                isDashboardAiChatMenuItem(item) &&
                  'rounded-lg bg-primary/12 font-semibold text-primary',
              )}
            >
              <MenuItemLabel item={item} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

function AdminNavbarMobileMenu() {
  const {trans} = useTrans();
  const navigate = useNavigate();
  const {hasPermission} = useAuth();

  // Фильтруем пункты меню по правам доступа
  const visibleItems = adminNavbarItems.filter(item => {
    if (!item.permissions || item.permissions.length === 0) {
      return true;
    }
    return item.permissions.every(permission => hasPermission(permission));
  });

  if (visibleItems.length === 0) {
    return null;
  }

  const handleItemClick = (item: (typeof adminNavbarItems)[0]) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <MenuTrigger>
      <IconButton
        className="md:hidden"
        aria-label={trans({message: 'Toggle menu'})}
      >
        <MenuIcon />
      </IconButton>
      <Menu>
        {visibleItems.map(item => {
          const Icon = item.icon;
          return (
            <Item
              value={item.action}
              onSelected={() => handleItemClick(item)}
              key={item.action}
              startIcon={Icon && <Icon size={18} />}
            >
              <Trans message={item.label} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
